import { Alert, StyledReactSelect } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import {
  OfferLetterTemplateForStage,
  OfferLetterTemplatesStage as OfferLetterTemplatesStageAttrs,
  WorkflowDocumentSigningStage,
  WorkflowStageDetail,
} from 'api-clients/monolith';
import produce from 'immer';
import React, { Dispatch, SetStateAction, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import { components as reactSelectComponents } from 'react-select';

import { CONTRACT_TYPE_LABELS } from 'containers/OfferLetterTemplates/constants';
import { ContractType } from 'containers/OfferLetterTemplates/types';
import { StageSettingCard } from 'containers/WorkflowEditor/components/StageSettingCard';
import { StageDetailPatchResponse } from 'containers/WorkflowEditor/contexts/stageContext';

import { collectGroupErrorMessages } from '../Documents/errorUtils';
import { messages } from './messages';
import { useStyles } from './styles';

// react-select v3 forwards `aria-label` and `aria-labelledby` to its inner
// input but has no `aria-describedby` support at all (verified: zero references
// in the pinned dist). Passing the prop straight through to StyledReactSelect
// silently drops it, so the association has to be injected via a wrapped Input.
//
// Without this the amber "this template was deactivated" hint is visible-only:
// the "(deactivated)" suffix rides in the option label so a screen reader
// announces the STATE as part of the value, but the recovery instruction —
// what to actually do about it — never reaches the user focused on the picker
// (WCAG 1.3.1; review on #2211).
//
// Ideally this lives in fountain-ui-components so every consumer gets it; that
// is a shared-library change with its own build, so it is noted on the PR for
// the accessibility initiative rather than folded into this ticket.
// The cast is at the one real impedance mismatch: StyledReactSelect types
// `components` as Record<string, ComponentType<unknown>>, which is looser than
// react-select's own precise Input props. The inner component stays fully typed.
//
// Cached per hint id: react-select swaps in a replacement component by function
// identity, so returning a fresh wrapper on every render would unmount and
// remount the inner <input> each render (dropping focus). The id set is the
// fixed contract-type buckets, so the cache is bounded.
const describedByInputCache = new Map<
  string,
  Record<string, React.ComponentType<unknown>>
>();
const describedByInput = (
  describedById: string,
): Record<string, React.ComponentType<unknown>> => {
  let cached = describedByInputCache.get(describedById);
  if (!cached) {
    cached = {
      Input: (
        inputProps: React.ComponentProps<typeof reactSelectComponents.Input>,
      ) => (
        <reactSelectComponents.Input
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...inputProps}
          aria-describedby={describedById}
        />
      ),
    } as unknown as Record<string, React.ComponentType<unknown>>;
    describedByInputCache.set(describedById, cached);
  }
  return cached;
};

export interface OfferLetterTemplatesProps {
  stage: WorkflowDocumentSigningStage;
  setStage: Dispatch<SetStateAction<WorkflowStageDetail>>;
  updateStageResult: StageDetailPatchResponse;
}

// The fixed contract types (single source of truth: CONTRACT_TYPE_LABELS) plus,
// only when relevant, an "unspecified" bucket for templates with no
// contract_type set — mirrors the backend's DocumentSigningStage
// #offer_letter_templates_unique_per_contract_type validation, which treats nil
// as its own conflicting bucket too.
const CONTRACT_TYPES = Object.keys(CONTRACT_TYPE_LABELS) as ContractType[];

interface SelectOption {
  label: string;
  value: number | null;
}

export const OfferLetterTemplates: VFC<OfferLetterTemplatesProps> = ({
  stage,
  setStage,
  updateStageResult,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const { accountSlug } = useParams<{ accountSlug: string }>();

  const availableTemplates: OfferLetterTemplateForStage[] =
    stage.additional_info.available_offer_letter_templates ?? [];
  const assignments: OfferLetterTemplatesStageAttrs[] =
    stage.additional_info.offer_letter_templates_stages_attributes ?? [];

  const hasUnspecifiedTemplates = availableTemplates.some(
    template => !template.contract_type,
  );
  const buckets: (ContractType | null)[] = hasUnspecifiedTemplates
    ? [...CONTRACT_TYPES, null]
    : CONTRACT_TYPES;

  // AXHE-3836: the payload now also carries any template this stage is still
  // assigned to after being deactivated (`active: false`). Those must stay in
  // `templateIdsForBucket` — both the "what's selected" lookup and the
  // mark-for-destruction pass in `handleChange` key off it, so excluding them
  // is exactly what made a stale assignment render as "None" and be impossible
  // to clear. They are deliberately kept out of the *pickable* options below,
  // so a deactivated template can be removed but never newly assigned.
  const templateIdsForBucket = (bucket: ContractType | null): number[] =>
    availableTemplates
      .filter(template => (template.contract_type ?? null) === bucket)
      .map(template => template.id)
      .filter((id): id is number => id != null);

  const isInactive = (template: OfferLetterTemplateForStage): boolean =>
    template.active === false;

  const selectedTemplateIdForBucket = (
    bucket: ContractType | null,
  ): number | null => {
    const bucketTemplateIds = new Set(templateIdsForBucket(bucket));
    const active = assignments.find(
      assignment =>
        !assignment._destroy &&
        assignment.offer_letter_template_id != null &&
        bucketTemplateIds.has(assignment.offer_letter_template_id),
    );
    return active?.offer_letter_template_id ?? null;
  };

  const handleChange =
    (bucket: ContractType | null) => (option: SelectOption | null) => {
      setStage(
        produce(stage, draftStage => {
          const bucketTemplateIds = new Set(templateIdsForBucket(bucket));
          const currentAttrs =
            draftStage.additional_info
              .offer_letter_templates_stages_attributes ?? [];

          const cleaned = currentAttrs.reduce<OfferLetterTemplatesStageAttrs[]>(
            (acc, assignment) => {
              const belongsToBucket =
                !assignment._destroy &&
                assignment.offer_letter_template_id != null &&
                bucketTemplateIds.has(assignment.offer_letter_template_id);

              if (!belongsToBucket) {
                acc.push(assignment);
                return acc;
              }

              // Persisted assignments need `_destroy` to remove server-side;
              // ones added client-side this session (no `id` yet) can just
              // be dropped from the array.
              if (assignment.id != null) {
                acc.push({ ...assignment, _destroy: true });
              }
              return acc;
            },
            [],
          );

          if (option?.value != null) {
            cleaned.push({ offer_letter_template_id: option.value });
          }

          draftStage.additional_info.offer_letter_templates_stages_attributes =
            cleaned;
        }),
      );
    };

  const errors =
    (updateStageResult.isError && updateStageResult.error?.errors) || undefined;
  const conflictErrorMessages = collectGroupErrorMessages(errors, key =>
    key.startsWith('offer_letter_templates_stages'),
  );

  const bucketLabel = (bucket: ContractType | null): string =>
    bucket
      ? intl.formatMessage(CONTRACT_TYPE_LABELS[bucket])
      : intl.formatMessage(messages.noContractType);

  return (
    <StageSettingCard
      title={<FormattedMessage {...messages.offerLetterTemplates} />}
      variant="default"
    >
      {conflictErrorMessages.length > 0 && (
        <Alert fullWidth type="danger" className={styles.errorsAlert}>
          <span>{conflictErrorMessages.join(', ')}</span>
        </Alert>
      )}
      {availableTemplates.length === 0 ? (
        <Typography variant="body2">
          <FormattedMessage
            {...messages.noTemplatesYet}
            values={{
              link: (
                <Link
                  className={styles.noTemplatesLink}
                  to={`/${accountSlug}/offer_letter_templates`}
                >
                  <FormattedMessage {...messages.createTemplatesLink} />
                </Link>
              ),
            }}
          />
        </Typography>
      ) : (
        buckets.map(bucket => {
          const selectedId = selectedTemplateIdForBucket(bucket);
          const bucketTemplates = availableTemplates.filter(
            template => (template.contract_type ?? null) === bucket,
          );
          // A deactivated template is offered only while it is the current
          // selection — so the recruiter can see and clear it, but can't newly
          // assign one.
          const selectableTemplates = bucketTemplates.filter(
            template => !isInactive(template) || template.id === selectedId,
          );
          const options: SelectOption[] = [
            { label: intl.formatMessage(messages.none), value: null },
            ...selectableTemplates.map(template => ({
              label: isInactive(template)
                ? intl.formatMessage(messages.inactiveTemplateOption, {
                    title: template.title ?? '',
                  })
                : (template.title ?? ''),
              value: template.id ?? null,
            })),
          ];
          const selectedIsInactive = bucketTemplates.some(
            template => template.id === selectedId && isInactive(template),
          );
          // Ties the amber recovery hint to the select programmatically. The
          // "(deactivated)" suffix rides along in the option label, so a screen
          // reader announces the STATE as part of the value — but the
          // what-to-do-about-it instruction lived only on screen. Without
          // aria-describedby a screen-reader user focused on the picker never
          // hears it, on the exact row where they most need guidance
          // (WCAG 1.3.1; review on #2211).
          const inactiveHintId = `offer-letter-inactive-hint-${bucket ?? 'unspecified'}`;

          return (
            <div className={styles.bucketGroup} key={bucket ?? 'unspecified'}>
              <div className={styles.row}>
                <Typography
                  className={styles.contractTypeLabel}
                  variant="body2"
                >
                  {bucketLabel(bucket)}
                </Typography>
                <StyledReactSelect
                  className={styles.select}
                  aria-label={intl.formatMessage(
                    messages.selectTemplateAriaLabel,
                    { contractType: bucketLabel(bucket) },
                  )}
                  components={
                    selectedIsInactive
                      ? describedByInput(inactiveHintId)
                      : undefined
                  }
                  onChange={handleChange(bucket)}
                  options={options}
                  value={
                    options.find(option => option.value === selectedId) ??
                    options[0]
                  }
                />
              </div>
              {selectedIsInactive && (
                <Typography
                  className={styles.inactiveHint}
                  id={inactiveHintId}
                  variant="body2"
                >
                  <FormattedMessage {...messages.inactiveTemplateHint} />
                </Typography>
              )}
            </div>
          );
        })
      )}
    </StageSettingCard>
  );
};
